import React, { useState } from "react";
import { Check, Copy, Key, ShieldCheck, Trash2, Wallet, X } from "lucide-react";

export default function SettingsModal({
  isOpen,
  activeTab,
  setActiveTab,
  keys,
  tokenBalance,
  webUsage,
  apiUsage,
  loading,
  newKeyData,
  copySuccess,
  setCopySuccess,
  setNewKeyData,
  setIsSettingsOpen,
  createKey,
  changePassword,
  createCheckout,
  billingMessage,
}) {
  const [passwordForm, setPasswordForm] = useState({ oldPassword: "", newPassword: "", confirmPassword: "" });
  const [passwordMessage, setPasswordMessage] = useState(null);

  if (!isOpen) return null;

  const visibleKeys = keys.filter((key) => (key[3] || "manual") === "manual");

  const formatReset = (timestamp) => {
    if (!timestamp) return "--";
    const date = new Date(timestamp * 1000);
    return date.toLocaleString([], { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  };

  const formatResetCountdown = (timestamp) => {
    if (!timestamp) return "等待同步";
    const seconds = Math.max(0, Math.ceil(timestamp - Date.now() / 1000));
    const totalMinutes = Math.max(1, Math.ceil(seconds / 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours <= 0) return `${totalMinutes} 分钟后重置`;
    if (minutes === 0) return `${hours} 小时后重置`;
    return `${hours} 小时 ${minutes} 分钟后重置`;
  };

  const formatLimit = (quota, unit) => {
    if (!quota) return "--";
    if (quota.limit === null) return "不限";
    return `${quota.remaining?.toLocaleString() ?? 0} / ${quota.limit.toLocaleString()} ${unit}`;
  };

  const formatUsed = (quota, unit) => {
    if (!quota) return "--";
    if (quota.limit === null) return "不限";
    return `${quota.used?.toLocaleString() ?? 0} / ${quota.limit.toLocaleString()} ${unit}`;
  };

  const submitPasswordChange = async () => {
    setPasswordMessage(null);
    if (!passwordForm.oldPassword) {
      setPasswordMessage({ success: false, msg: "请输入原密码" });
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setPasswordMessage({ success: false, msg: "新密码至少需要 6 位" });
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordMessage({ success: false, msg: "两次输入的新密码不一致" });
      return;
    }
    const result = await changePassword(passwordForm);
    setPasswordMessage(result);
    if (result.success) {
      setPasswordForm({ oldPassword: "", newPassword: "", confirmPassword: "" });
    }
  };

  return (
    <>
      <div className="modal-backdrop">
        <section className="settings-modal" role="dialog" aria-modal="true" aria-label="账户设置">
          <header className="modal-header">
            <h2>账户设置</h2>
            <button type="button" className="icon-button" onClick={() => setIsSettingsOpen(false)} aria-label="关闭设置">
              <X size={20} />
            </button>
          </header>
          <div className="modal-body">
            <nav className="settings-nav">
              <button type="button" className={activeTab === "keys" ? "active" : ""} onClick={() => setActiveTab("keys")}>
                <Key size={18} />
                <span>API 密钥</span>
              </button>
              <button type="button" className={activeTab === "billing" ? "active" : ""} onClick={() => setActiveTab("billing")}>
                <Wallet size={18} />
                <span>会员充值</span>
              </button>
              <button type="button" className={activeTab === "security" ? "active" : ""} onClick={() => setActiveTab("security")}>
                <ShieldCheck size={18} />
                <span>账号安全</span>
              </button>
            </nav>
            <div className="settings-content">
              {activeTab === "keys" && (
                <div className="settings-section">
                  <div className="section-title-row">
                    <h3>密钥管理</h3>
                    <button type="button" className="primary-small" onClick={createKey} disabled={loading}>
                      创建新密钥
                    </button>
                  </div>
                  <div className="key-list">
                    {visibleKeys.length === 0 && <p className="muted">暂无 API 密钥。网页对话使用登录状态，无需创建密钥。</p>}
                    {visibleKeys.map((key, index) => (
                      <div key={`${key[0]}-${index}`} className="key-row">
                        <span>{key[0]}</span>
                        <button type="button" aria-label="删除密钥">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {activeTab === "billing" && (
                <div className="settings-section">
                  <div className="membership-card">
                    <div>
                      <span>当前会员</span>
                      <strong>{webUsage?.membership_tier || "free"}</strong>
                    </div>
                    <div className="membership-actions">
                      {["free", "plus", "pro"].map((tier) => (
                        <button key={tier} type="button" className={webUsage?.membership_tier === tier ? "active" : ""} disabled>
                          {tier}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="quota-grid">
                    <div className="quota-card">
                      <span>网页聊天额度</span>
                      <strong>{formatLimit(webUsage?.chat, "tokens")}</strong>
                      <small>{formatResetCountdown(webUsage?.chat?.reset_at)} · {formatReset(webUsage?.chat?.reset_at)}</small>
                    </div>
                    <div className="quota-card">
                      <span>网页生图额度</span>
                      <strong>{formatLimit(webUsage?.image, "张")}</strong>
                      <small>{formatResetCountdown(webUsage?.image?.reset_at)} · {formatReset(webUsage?.image?.reset_at)}</small>
                    </div>
                  </div>
                  <div className="api-usage-card">
                    <div>
                      <span>API Key 调用额度</span>
                      <strong>{formatLimit(apiUsage?.api_token, "tokens")}</strong>
                      <small>已用 {formatUsed(apiUsage?.api_token, "tokens")}</small>
                    </div>
                    <div className="reset-pill">
                      <span>{formatResetCountdown(apiUsage?.api_token?.reset_at)}</span>
                      <small>{formatReset(apiUsage?.api_token?.reset_at)}</small>
                    </div>
                  </div>
                  {tokenBalance > 0 && <p className="legacy-balance-note">旧余额记录：{tokenBalance.toLocaleString()} tokens，不参与当前 API 调用额度。</p>}
                  {billingMessage?.msg && <p className={`auth-message ${billingMessage.success ? "success" : "error"}`}>{billingMessage.msg}</p>}
                  <div className="recharge-grid">
                    {[
                      { plan: "plus", title: "开通 Plus", desc: "30 天会员，网页聊天不限，图片额度提升" },
                      { plan: "pro", title: "开通 Pro", desc: "30 天会员，更高图片额度，适合高频使用" },
                    ].map((item) => (
                      <button key={item.plan} type="button" onClick={() => createCheckout(item.plan)} disabled={loading}>
                        <span>{item.title}</span>
                        <strong>{item.plan.toUpperCase()}</strong>
                        <small>{item.desc}</small>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {activeTab === "security" && (
                <div className="settings-section">
                  <div className="section-title-row">
                    <div>
                      <h3>修改密码</h3>
                      <p className="muted security-section-copy">验证原密码后更新。修改成功会保留当前设备，并退出其他登录设备。</p>
                    </div>
                  </div>
                  <div className="security-form">
                    <label>
                      <span>原密码</span>
                      <input
                        type="password"
                        autoComplete="current-password"
                        value={passwordForm.oldPassword}
                        onChange={(event) => setPasswordForm({ ...passwordForm, oldPassword: event.target.value })}
                      />
                    </label>
                    <label>
                      <span>新密码</span>
                      <input
                        type="password"
                        autoComplete="new-password"
                        value={passwordForm.newPassword}
                        onChange={(event) => setPasswordForm({ ...passwordForm, newPassword: event.target.value })}
                      />
                    </label>
                    <label>
                      <span>确认新密码</span>
                      <input
                        type="password"
                        autoComplete="new-password"
                        value={passwordForm.confirmPassword}
                        onChange={(event) => setPasswordForm({ ...passwordForm, confirmPassword: event.target.value })}
                        onKeyDown={(event) => event.key === "Enter" && submitPasswordChange()}
                      />
                    </label>
                    {passwordMessage?.msg && (
                      <p className={`auth-message ${passwordMessage.success ? "success" : "error"}`}>{passwordMessage.msg}</p>
                    )}
                    <button type="button" className="primary-small security-submit" onClick={submitPasswordChange} disabled={loading}>
                      保存新密码
                    </button>
                  </div>
                  <p className="security-recovery-note">忘记原密码时，请退出登录，在登录页使用绑定手机号验证后重置。</p>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>

      {newKeyData && (
        <div className="modal-backdrop modal-backdrop-strong">
          <section className="new-key-modal" role="dialog" aria-modal="true" aria-label="新密钥">
            <div className="success-icon">
              <ShieldCheck size={32} />
            </div>
            <h2>密钥已生成</h2>
            <p>请妥善保存。出于安全考量，此密钥仅会显示一次。</p>
            <div className="new-key-value">
              <span>{newKeyData}</span>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(newKeyData);
                  setCopySuccess(true);
                  setTimeout(() => setCopySuccess(false), 2000);
                }}
                aria-label="复制密钥"
              >
                {copySuccess ? <Check size={18} /> : <Copy size={18} />}
              </button>
            </div>
            <button type="button" className="primary-button" onClick={() => setNewKeyData(null)}>
              确认已保存
            </button>
          </section>
        </div>
      )}
    </>
  );
}
